/*
 * This file is part of the Det Norske Teatret application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React, { type ComponentProps } from 'react';
import Picture from 'components/Picture/Picture';
import useAnimation from './animations';
import './SideImage.scss';

const DEFAULT_IMAGE: ComponentProps<typeof Picture> = {};

interface SideImageProps {
  layout?: string;
  image?: ComponentProps<typeof Picture>;
  title?: string;
  excerpt?: string;
  author?: string;
  photographer?: string;
}

/**
 * This is the SideImage component.
 *
 * @author Thomas Sømoen <thomas@apt.no>
 */
function SideImage({
  layout = 'left',
  image = DEFAULT_IMAGE,
  title = '',
  excerpt = '',
  author,
  photographer,
}: SideImageProps) {
  const [ref] = useAnimation();

  function renderCopyright() {
    if (!author && !photographer) return null;

    function renderAuthor() {
      if (!author) return null;

      return <span className="author">{author}</span>;
    }

    function renderDevider() {
      if (!(author && photographer)) return null;

      return ' // ';
    }

    function renderPhotographer() {
      if (!photographer) return null;
      return <span className="photo">{photographer}</span>;
    }

    return (
      <div key="copyright" className="copyright">
        {renderAuthor()}
        {renderDevider()}
        {renderPhotographer()}
      </div>
    );
  }

  return (
    <header ref={ref} className={`side-image layout-${layout}`}>
      <div className="image">
        <div>
          <Picture {...image} />
        </div>
      </div>
      <div className="text">
        <h1 dangerouslySetInnerHTML={{ __html: title }} />
        <div key="excerpt" className="excerpt">
          <p dangerouslySetInnerHTML={{ __html: excerpt }} />
        </div>
        {renderCopyright()}
      </div>
    </header>
  );
}

export default SideImage;
